/**
 * @module theme
 * @description Core theme bootstrap — runs on every page load.
 *
 * Responsibilities:
 * - Initializes product cards (variant swatches, quick-view triggers)
 * - Initializes [data-slider] roots not managed by section scripts (see init())
 * - Sets up global event delegation for [data-triggers-event] click handlers
 *
 * Loaded as the first `<script type="module">` in scripts.liquid.
 * This is a page-level entry point, not an importmap library.
 *
 * @see {@link @stardust/product-card}
 * @see {@link @stardust/slider}
 */
import { initProductCards } from "@stardust/product-card";
import Slider from "@stardust/slider";
import "@stardust/footer-phone-input";
import "@stardust/klaviyo-footer-subscribe";

/**
 * Core theme initialization
 */
function init() {
  // Initialize product cards
  initProductCards();

  // Initialize sliders that are not already owned by a section script (rebuy, recently viewed,
  // UGC, store messages each call `new Slider` with their own options). A second Slider with
  // default snap:true on the same root stacks click handlers and breaks prev (snap scroll + free scroll fight).
  document.querySelectorAll('[data-slider]').forEach((el) => {
    // Explicit opt-out (section scripts own Slider) — survives DOM/ancestor quirks.
    if (el.hasAttribute('data-slider-manual')) return;
    if (
      el.closest(
        '[data-rebuy-slider-section], [data-recently-viewed-section], [data-product-ugc], [data-section-type="store-messages"]'
      )
    ) {
      return;
    }

    const autoplay = el.dataset.autoplay === 'true';
    const interval = Number.parseInt(el.dataset.interval || '', 10);

    new Slider(el, {
      autoplay,
      interval: Number.isFinite(interval) ? interval : 5000
    });
  });
}

// Event delegation for data-triggers-event attributes
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-triggers-event]');
  if (!trigger) return;

  e.preventDefault();
  const eventName = trigger.dataset.triggersEvent;
  const params = trigger.dataset.triggersEventParams;

  document.dispatchEvent(new CustomEvent(eventName, {
    detail: params ? JSON.parse(params) : {},
    bubbles: true
  }));
});

// Checkout button loading state — shows spinner while form POSTs + redirects
document.addEventListener('submit', (e) => {
  const btn = e.target.querySelector('[data-checkout-submit]');
  if (btn) {
    btn.setAttribute('data-checkout-loading', '');
    btn.setAttribute('aria-busy', 'true');
  }
});

// Initialize
init();
